<template>
  <div class="media-library">
    <div class="media-header">
      <h2>Biblioteca de Mídias</h2>
      <p class="media-description">
        Faça upload de imagens e documentos para usar como anexos em emails e mídias no WhatsApp.
      </p>
    </div>

    <!-- Toolbar -->
    <div class="media-toolbar">
      <div class="media-filtros">
        <button
          v-for="filtro in filtros"
          :key="filtro.value"
          class="filtro-btn"
          :class="{ active: filtroAtivo === filtro.value }"
          @click="filtroAtivo = filtro.value"
        >
          <i :class="filtro.icon"></i>
          {{ filtro.label }}
        </button>
      </div>
      <button class="btn-upload" @click="abrirUpload">
        <i class="fas fa-cloud-upload-alt"></i>
        Upload
      </button>
      <input
        ref="inputArquivo"
        type="file"
        class="input-hidden"
        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx"
        multiple
        @change="handleArquivosSelecionados"
      />
    </div>

    <!-- Upload Progress -->
    <div v-if="uploading" class="upload-progress">
      <div class="upload-progress-bar">
        <div class="upload-progress-fill" :style="{ width: uploadProgress + '%' }"></div>
      </div>
      <span class="upload-progress-text">Enviando... {{ uploadProgress }}%</span>
    </div>

    <!-- Loading -->
    <div v-if="loading && !midias.length" class="media-loading">
      <div class="spinner"></div>
      <p>Carregando mídias...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!midiasFiltradas.length" class="media-empty">
      <i class="fas fa-photo-video"></i>
      <p v-if="midias.length">Nenhuma mídia encontrada com o filtro selecionado.</p>
      <p v-else>Nenhuma mídia cadastrada. Clique em "Upload" para adicionar.</p>
    </div>

    <!-- Grid -->
    <div v-else class="media-grid">
      <div
        v-for="media in midiasFiltradas"
        :key="media.id"
        class="media-card"
      >
        <div class="media-thumb">
          <img
            v-if="media.tipo === 'imagem'"
            :src="media.url"
            :alt="media.nome"
            class="thumb-img"
            loading="lazy"
          />
          <div v-else class="thumb-icon">
            <i :class="getIconeDocumento(media.mime_type)"></i>
          </div>
        </div>
        <div class="media-info">
          <span class="media-nome" :title="media.nome">{{ media.nome }}</span>
          <div class="media-meta">
            <span class="media-tamanho">{{ media.tamanho_formatado }}</span>
            <span class="media-tipo-badge" :class="media.tipo">{{ media.tipo }}</span>
          </div>
          <span class="media-data">{{ formatarData(media.created_at) }}</span>
        </div>
        <div class="media-actions">
          <button class="btn-action btn-delete" title="Excluir" @click="confirmarExclusao(media)">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'

const midias = ref([])
const loading = ref(false)
const uploading = ref(false)
const uploadProgress = ref(0)
const filtroAtivo = ref('')
const inputArquivo = ref(null)

const filtros = [
  { value: '', label: 'Todos', icon: 'fas fa-th' },
  { value: 'imagem', label: 'Imagens', icon: 'fas fa-image' },
  { value: 'documento', label: 'Documentos', icon: 'fas fa-file-alt' },
]

const midiasFiltradas = computed(() => {
  if (!filtroAtivo.value) return midias.value
  return midias.value.filter(m => m.tipo === filtroAtivo.value)
})

function getIconeDocumento(mimeType) {
  if (mimeType === 'application/pdf') return 'fas fa-file-pdf'
  if (mimeType?.includes('word') || mimeType?.includes('.doc')) return 'fas fa-file-word'
  if (mimeType?.includes('sheet') || mimeType?.includes('.xls') || mimeType?.includes('excel')) return 'fas fa-file-excel'
  return 'fas fa-file'
}

function formatarData(data) {
  if (!data) return ''
  return new Date(data).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function abrirUpload() {
  inputArquivo.value?.click()
}

async function handleArquivosSelecionados(event) {
  const files = event.target.files
  if (!files.length) return

  uploading.value = true
  uploadProgress.value = 0

  const total = files.length
  let completed = 0

  for (const file of files) {
    try {
      const formData = new FormData()
      formData.append('arquivo', file)

      const { data } = await axios.post('/api/notificacoes/midias', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      midias.value.unshift(data.media)
      completed++
      uploadProgress.value = Math.round((completed / total) * 100)
    } catch (err) {
      const msg = err.response?.data?.message || `Erro ao enviar ${file.name}`
      Swal.fire('Erro!', msg, 'error')
      completed++
      uploadProgress.value = Math.round((completed / total) * 100)
    }
  }

  uploading.value = false
  uploadProgress.value = 0
  inputArquivo.value.value = ''

  if (completed === total) {
    Swal.fire({
      icon: 'success',
      title: 'Upload concluído!',
      text: `${total} arquivo(s) enviado(s) com sucesso.`,
      timer: 2000,
      showConfirmButton: false,
    })
  }
}

async function confirmarExclusao(media) {
  const result = await Swal.fire({
    title: 'Excluir mídia?',
    html: `<p>A mídia <strong>${media.nome}</strong> será excluída permanentemente.</p>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#f44336',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sim, excluir',
    cancelButtonText: 'Cancelar',
  })

  if (!result.isConfirmed) return

  try {
    await axios.delete(`/api/notificacoes/midias/${media.id}`)
    midias.value = midias.value.filter(m => m.id !== media.id)
    Swal.fire({
      icon: 'success',
      title: 'Excluída!',
      text: 'Mídia excluída com sucesso.',
      timer: 1500,
      showConfirmButton: false,
    })
  } catch (err) {
    const msg = err.response?.data?.message || 'Erro ao excluir mídia.'
    Swal.fire('Erro!', msg, 'error')
  }
}

async function carregarMidias() {
  loading.value = true
  try {
    const { data } = await axios.get('/api/notificacoes/midias')
    midias.value = data
  } catch (err) {
    console.error('Erro ao carregar mídias:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  carregarMidias()
})
</script>

<style scoped>
.media-library {
  padding: 25px;
}

.media-header h2 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #333;
}

.media-description {
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #666;
}

/* Toolbar */
.media-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.media-filtros {
  display: flex;
  gap: 6px;
  flex: 1;
}

.filtro-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.filtro-btn:hover {
  border-color: #4caf50;
  color: #333;
}

.filtro-btn.active {
  background: #4caf50;
  border-color: #4caf50;
  color: white;
}

.btn-upload {
  padding: 10px 20px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}

.btn-upload:hover {
  background: #43a047;
}

.input-hidden {
  display: none;
}

/* Upload Progress */
.upload-progress {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.upload-progress-bar {
  flex: 1;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.upload-progress-fill {
  height: 100%;
  background: #4caf50;
  transition: width 0.3s ease;
  border-radius: 4px;
}

.upload-progress-text {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

/* Loading */
.media-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Empty */
.media-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.media-empty i {
  font-size: 48px;
  margin-bottom: 12px;
}

.media-empty p {
  font-size: 15px;
  margin: 0;
}

/* Grid */
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.media-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
}

.media-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.media-thumb {
  width: 100%;
  height: 140px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumb-icon i {
  font-size: 48px;
  color: #999;
}

.thumb-icon i.fa-file-pdf { color: #f44336; }
.thumb-icon i.fa-file-word { color: #1976d2; }
.thumb-icon i.fa-file-excel { color: #388e3c; }

.media-info {
  padding: 12px;
}

.media-nome {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 6px;
}

.media-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.media-tamanho {
  font-size: 12px;
  color: #888;
}

.media-tipo-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
}

.media-tipo-badge.imagem {
  background: #e3f2fd;
  color: #1976d2;
}

.media-tipo-badge.documento {
  background: #fff3e0;
  color: #e65100;
}

.media-tipo-badge.video {
  background: #f3e5f5;
  color: #7b1fa2;
}

.media-data {
  font-size: 11px;
  color: #aaa;
}

.media-actions {
  display: flex;
  justify-content: flex-end;
  padding: 0 12px 12px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: all 0.2s;
}

.btn-delete {
  background: #fce4ec;
  color: #c62828;
}

.btn-delete:hover {
  background: #ef9a9a;
  color: #b71c1c;
}

/* Dark mode */
.dark-mode .media-header h2 {
  color: #f1f1f1;
}

.dark-mode .media-description {
  color: #aaa;
}

.dark-mode .filtro-btn {
  background: #2a2a2a;
  border-color: #444;
  color: #aaa;
}

.dark-mode .filtro-btn:hover {
  border-color: #4caf50;
  color: #eee;
}

.dark-mode .filtro-btn.active {
  background: #4caf50;
  color: white;
}

.dark-mode .media-card {
  background: #252525;
  border-color: #3a3a3a;
}

.dark-mode .media-thumb {
  background: #1e1e1e;
}

.dark-mode .thumb-icon i {
  color: #666;
}

.dark-mode .media-nome {
  color: #e0e0e0;
}

.dark-mode .media-tamanho {
  color: #777;
}

.dark-mode .media-data {
  color: #666;
}

.dark-mode .media-empty {
  color: #666;
}

.dark-mode .upload-progress-bar {
  background: #333;
}

.dark-mode .btn-delete {
  background: #3a1a1a;
  color: #ef9a9a;
}

.dark-mode .btn-delete:hover {
  background: #4a2020;
}

@media (max-width: 768px) {
  .media-library {
    padding: 15px;
  }

  .media-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .media-filtros {
    flex: none;
  }

  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }

  .media-thumb {
    height: 100px;
  }
}
</style>
